import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react'
import {
  findSketchElementFromUuid,
  isCanvasEntityElement,
  isPageElement,
  SketchElement,
} from '@sketch/scene-model'
import { noop } from '@sketch/utils'

/**
 * What is selected on the canvas, as stored.
 *
 * Internal to this module - callers work in elements and entity ids, not in
 * this shape.
 *
 * `uuid` is always set. `element` is the same selection as a scene element, and
 * is only set while the inspector's scene tree exists: the tree is built on
 * demand, so view mode has no elements to hand.
 *
 * That asymmetry is safe because of what each mode can select. View mode
 * selects only top-level frames and Sections, which are never inside a symbol
 * instance and so have a `uuid` that identifies them on its own. The inspector
 * can select any layer at any depth, where the layer UUID is *not* unique (see
 * `SketchElement.elementUUID`) - but it always has the tree, so those
 * selections carry the element and never rely on the uuid alone.
 */
type CanvasSelection = {
  uuid: string
  element: SketchElement | null
}

type CanvasSelectionContextValue = {
  selection: CanvasSelection | null
  setSelection: (selection: CanvasSelection | null) => void
}

/**
 * Nothing selected, and selecting does nothing.
 *
 * The context carries this rather than `undefined` so consumers can read it
 * unconditionally - it is threaded through the overlays, the inspector and the
 * copy-link shortcut, and every one of them was carrying its own `?.` and
 * `?? noop` for a case none of them can do anything useful about.
 *
 * A no-op rather than a throw because a canvas can legitimately render before
 * anything can be selected on it. What must not happen is a canvas that lets
 * you select things mounting without a provider - then selection silently does
 * nothing - so every view that mounts `FrameInspectorProvider` or the entity
 * overlays mounts `CanvasSelectionProvider` above it.
 */
const NO_SELECTION: CanvasSelectionContextValue = {
  selection: null,
  setSelection: noop,
}

const CanvasSelectionContext =
  createContext<CanvasSelectionContextValue>(NO_SELECTION)

type CanvasSelectionProviderProps = {
  children: React.ReactNode
}

/**
 * The canvas's selection, shared by everything that reads or writes it: the
 * entity overlays, the inspector, the layer tree and the copy-link shortcut.
 *
 * One store rather than two. The inspector used to keep its own
 * `selectedElement` alongside this one and sync between them, which meant a
 * selection made in one mode could not be seen by the other - selecting a frame
 * and then opening the Inspect tab lost it - and that each side had to know how
 * to write to the other's.
 */
export function CanvasSelectionProvider({
  children,
}: CanvasSelectionProviderProps) {
  const [selection, setSelection] = useState<CanvasSelection | null>(null)

  const value = useMemo(
    () => ({
      selection,
      setSelection,
    }),
    [selection]
  )

  return (
    <CanvasSelectionContext.Provider value={value}>
      {children}
    </CanvasSelectionContext.Provider>
  )
}

/**
 * The canvas selection. Always defined: outside a provider it reads as nothing
 * selected, and selecting is a no-op.
 *
 * The stored shape stays inside this module. Callers get the selection as an
 * element, or - for the overlays, which have no elements - as the id of the
 * selected entity, and write it the same two ways.
 *
 * One hook rather than a getter and a setter: both would subscribe to this same
 * context, whose value changes whenever the selection does, so splitting them
 * would not have saved a single render.
 */
export function useCanvasSelection() {
  const { selection, setSelection } = useContext(CanvasSelectionContext)

  const selectElement = useCallback(
    (element: SketchElement | null) => {
      // Selecting the page is not allowed and clears the selection instead.
      // The guard lives here so it holds for every writer rather than only the
      // one that happens to own the state.
      const selectable = element && !isPageElement(element) ? element : null

      setSelection(
        selectable
          ? { uuid: selectable.elementUUID, element: selectable }
          : null
      )
    },
    [setSelection]
  )

  /**
   * Select something known only by id, for callers with no scene element to
   * hand - the entity overlays, which work off the renderer's file lists and
   * exist whether or not the inspector has built a tree.
   *
   * `useResolveCanvasSelection` attaches the element when a tree appears.
   */
  const selectEntityByUUID = useCallback(
    (uuid: string) => setSelection({ uuid, element: null }),
    [setSelection]
  )

  return {
    /** The selected element, when the selection has one. Any layer, any depth. */
    selectedElement: selection?.element ?? null,
    /**
     * The id of the selected *entity* - a top-level frame or Section - and null
     * when what is selected is a layer inside one.
     *
     * The distinction matters to everything that treats the selection as a
     * navigable thing: the overlays draw entity chrome, and the copy-link
     * shortcut builds a frame or Section URL from this id. Handing either the
     * raw uuid would give them a layer's id dressed as an entity's.
     *
     * A selection with no element can only be an entity - nothing else is
     * selectable without a tree - so it counts.
     */
    selectedEntityUUID: toSelectedEntityUUID(selection),
    selectElement,
    selectEntityByUUID,
  }
}

function toSelectedEntityUUID(selection: CanvasSelection | null) {
  if (!selection) return null

  if (!selection.element) return selection.uuid

  return isCanvasEntityElement(selection.element) ? selection.uuid : null
}

/**
 * Fills in the scene element for a selection that only carries an id.
 *
 * The entity overlays select by id: they work off the renderer's file lists and
 * exist whether or not the inspector has built a scene tree, so they have no
 * element to hand. Whoever holds the tree resolves it, which is why this takes
 * one - it is the store's own upkeep, so it lives here rather than leaving the
 * raw selection exposed for someone else to patch.
 *
 * This is what carries a selection across the Inspect tab being opened. Select
 * a frame on the page canvas, open the inspector, and the selection is already
 * in the store waiting to be given its element; it used to be lost, because the
 * inspector kept its own separate selection state.
 *
 * Note the id is enough here. Only entities can be selected without a tree, and
 * an entity is top-level, so its id is unique - unlike a layer inside a symbol
 * instance, which is always selected with its element already attached.
 */
export function useResolveCanvasSelection(
  sketchSceneRootElement: SketchElement | null,
  isEnabled: boolean
) {
  const { selection, setSelection } = useContext(CanvasSelectionContext)

  const uuid = selection?.uuid
  const needsElement = Boolean(selection && !selection.element)

  useEffect(() => {
    if (!isEnabled || !needsElement || !uuid) {
      return
    }

    // The tree is built on demand and arrives a beat after the inspector opens,
    // so this runs again once it does.
    const element = findSketchElementFromUuid(sketchSceneRootElement, uuid)

    if (element) {
      setSelection({ uuid, element })
    }
  }, [isEnabled, needsElement, uuid, sketchSceneRootElement, setSelection])
}
